import { useParams } from '@tanstack/react-router';
import { useMemo, useState, useEffect, useRef } from 'react';
import { 
    ArrowLeft, Search, FileText, Activity, AlertTriangle, 
    Bug, Code, Shield, Loader2, GitBranch, Folder, FileCode, 
    ChevronDown, BarChart2, CheckCircle2, BrainCircuit, Sparkles, Zap
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button, Input, Card, Badge, Modal } from '@shared/ui';
import { Header } from '@widgets/header';
import { ExportButton } from '@features/export-report';
import { useProject, useProjectAnalysisSummary, useProjectReports, useDeepAnalysis, useGithubBranches, githubService, useJobStatus } from '@shared/api';
import { sileo } from 'sileo';
import { DirectCodeAnalysisModal } from '@features/direct-code-analysis';
import { AdvancedAnalysis } from '@features/analysis-visuals';
import { DropZone } from '@shared/ui';
import { useAnalysisStore } from '@shared/lib/store/analysisStore';
import { FileExplorer } from '@features/github-integration';

/**
 * Project Details Page
 * Shows comprehensive analysis for a single project
 */
export function ProjectDetailsPage() {
    const { t } = useTranslation();
    const { projectId } = useParams({ from: '/projects/$projectId' });
    const [searchQuery, setSearchQuery] = useState('');
    const [isSnippetModalOpen, setIsSnippetModalOpen] = useState(false);
    const [isBranchMenuOpen, setIsBranchMenuOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<'files' | 'analysis'>('files');
    const [selectedFileFilter, setSelectedFileFilter] = useState<string | null>(null);
    const [isResultsModalOpen, setIsResultsModalOpen] = useState(false);
    const branchMenuRef = useRef<HTMLDivElement>(null);

    // Global Store
    const activeJobId = useAnalysisStore(state => state.activeJobId);
    const setActiveJobId = useAnalysisStore(state => state.setActiveJobId);
    const localData = useAnalysisStore(state => state.projectData[projectId]);

    // Real API Hooks
    const { data: project, isLoading: isProjectLoading, isError: isProjectError } = useProject(projectId);
    const { data: summary, isLoading: isSummaryLoading, refetch: refetchSummary } = useProjectAnalysisSummary(projectId);
    const { data: reportsData, isLoading: isReportsLoading, refetch: refetchReports } = useProjectReports(projectId);
    const { mutate: runDeepAnalysis, isPending: isAnalyzing } = useDeepAnalysis();

    // Job Polling
    const { data: jobStatus, error: jobError, isError: isJobError } = useJobStatus(activeJobId || '', !!activeJobId);

    useEffect(() => {
        if (isJobError || jobError) {
            console.error('Job polling error:', jobError);
            sileo.error({
                title: 'Análisis no encontrado',
                description: 'No se pudo recuperar el estado del análisis. Es posible que el servidor haya reiniciado la tarea.',
                icon: <AlertTriangle className="size-4 text-error" />
            });
            setActiveJobId(null);
            return;
        }

        if (jobStatus?.status === 'completed') {
            sileo.success({
                title: 'Análisis Completado',
                description: 'Los resultados de Gemini están listos para ser revisados.',
                icon: <CheckCircle2 className="size-4 text-success" />
            });
            // No abrir automáticamente, dejar que el usuario lo haga desde el banner
            refetchSummary();
            refetchReports();
        } else if (jobStatus?.status === 'failed') {
            sileo.error({
                title: 'Error en Análisis',
                description: jobStatus.message || 'El análisis falló inesperadamente.'
            });
            setActiveJobId(null);
        }
    }, [jobStatus, refetchSummary, refetchReports]);

    // GitHub Integration
    const repoFullName = useMemo(() => {
        if (!project?.repository_url) return null;
        try {
            const url = new URL(project.repository_url);
            const path = url.pathname.substring(1); 
            return path.endsWith('.git') ? path.slice(0, -4) : path;
        } catch { return null; }
    }, [project]);

    const { data: branches } = useGithubBranches(repoFullName);
    const [selectedBranch, setSelectedBranch] = useState(project?.default_branch || 'main');

    useEffect(() => {
        if (project?.default_branch) setSelectedBranch(project.default_branch);
    }, [project]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (branchMenuRef.current && !branchMenuRef.current.contains(event.target as Node)) {
                setIsBranchMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Local Persistence

    const localStats = useMemo(() => {
        if (!localData) return { totalIssues: 0, critical: 0, high: 0, medium: 0, low: 0, healthScore: 0, coverage: 0 };
        const issues = localData.aggregatedIssues || [];
        const critical = issues.filter(i => (i.severity || '').toLowerCase() === 'critical').length;
        const high = issues.filter(i => (i.severity || '').toLowerCase() === 'high').length;
        const medium = issues.filter(i => (i.severity || '').toLowerCase() === 'medium').length;
        const low = issues.filter(i => (i.severity || '').toLowerCase() === 'low').length;
        const penalty = (critical * 15) + (high * 10) + (medium * 5) + (low * 1);
        const count = Math.max(1, localData.results?.length || 1);
        const healthScore = Math.max(0, Math.min(100, 100 - (penalty / count)));

        return {
            totalIssues: issues.length || 0,
            critical: critical || 0,
            high: high || 0,
            medium: medium || 0,
            low: low || 0,
            healthScore: isNaN(healthScore) ? 100 : healthScore,
            coverage: summary?.coverage || 0
        };
    }, [localData]);

    const [droppedFileName, setDroppedFileName] = useState('snippet.ts');
    const [droppedCode, setDroppedCode] = useState('');

    const handleFileDrop = (fileName: string, content: string) => {
        setDroppedCode(content);
        setDroppedFileName(fileName);
        setIsSnippetModalOpen(true);
        sileo.info({
            title: 'File Loaded',
            description: `${fileName} ready for analysis.`,
            icon: <FileText className="size-3.5" />
        });
    };

    const handleFileSelect = async (path: string) => {
        if (!repoFullName) return;
        const loader = sileo.action({
            title: 'Cargando archivo',
            description: `Obteniendo ${path} de GitHub...`,
            icon: <Loader2 className="animate-spin h-3.5 w-3.5" />
        });
        try {
            const res = await githubService.getFile(repoFullName, path, selectedBranch);
            setDroppedCode(res.content);
            setDroppedFileName(path);
            setIsSnippetModalOpen(true);
            sileo.dismiss(loader);
        } catch (err) {
            sileo.dismiss(loader);
            sileo.error({ title: 'Error de carga', description: 'No se pudo obtener el contenido.' });
        }
    };

    const files = useMemo(() => {
        const fileMap = new Map<string, { name: string; issues: number; coverage: number; debt: string }>();
        const latestReport = reportsData?.items?.[0];
        if (latestReport?.issues) {
            latestReport.issues.forEach(issue => {
                const path = issue.file_path || 'unknown';
                const existing = fileMap.get(path);
                if (existing) existing.issues += 1;
                else fileMap.set(path, { name: path, issues: 1, coverage: 0, debt: 'Auto' });
            });
        }
        if (localData?.results) {
            localData.results.forEach(result => {
                const path = result.fileName || 'snippet.ts';
                const existing = fileMap.get(path);
                if (existing) existing.issues = Math.max(existing.issues, result.issues.length || 0);
                else fileMap.set(path, { name: path, issues: result.issues.length || 0, coverage: summary?.coverage || 0, debt: 'Local' });
            });
        }
        return Array.from(fileMap.values());
    }, [reportsData, localData]);

    const filteredFiles = files.filter(f => f.name.toLowerCase().includes(searchQuery.toLowerCase()));

    const handleRunAnalysis = (paths: string[] = ['*']) => {
        runDeepAnalysis({ 
            project_id: projectId, 
            file_paths: paths, 
            branch: selectedBranch,
            analysis_types: ['all'] 
        }, {
            onSuccess: (data) => {
                if (data?.job_id) {
                    setActiveJobId(data.job_id);
                }
                const pathDesc = paths[0] === '*' ? `la rama ${selectedBranch}` : `la ruta: ${paths[0]}`;
                sileo.success({
                    title: 'Analysis Started',
                    description: `Análisis profundo iniciado para ${pathDesc}.`,
                    icon: <Activity className="size-3.5" />,
                });
            }
        });
    };

    if (isProjectLoading || isSummaryLoading) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <div className="flex items-center justify-center p-20">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            </div>
        );
    }

    if (isProjectError || !project) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <div className="container mx-auto px-6 py-12 text-center">
                    <h1 className="text-2xl font-bold text-error">{t('projectDetails.notFound')}</h1>
                    <Link to="/projects" className="text-primary hover:underline mt-4 inline-block">Volver a Proyectos</Link>
                </div>
            </div>
        );
    }

    const reportData = {
        project: {
            name: project.name,
            version: 'v2.4.1',
            date: project.updated_at ?? project.created_at,
            commit: project.repository_url ? selectedBranch : 'Direct Code',
            description: project.description ?? '',
            repositoryUrl: project.repository_url ?? undefined
        },
        metrics: {
            coverage: localStats.coverage || (summary?.coverage ?? 0),
            bugs: localStats.high || (summary?.issues_by_severity?.high ?? 0),
            technicalDebt: summary?.technical_debt_human_readable ?? 'Auto',
            vulnerabilities: localStats.critical || (summary?.issues_by_severity?.critical ?? 0),
            codeSmells: localStats.medium || (summary?.issues_by_severity?.medium ?? 0),
            healthScore: localStats.healthScore || (summary?.health_score ?? 0),
        },
        issues: (() => {
            const backendIssues = reportsData?.items?.[0]?.issues || [];
            const localIssues = localData?.aggregatedIssues || [];
            return [...localIssues, ...backendIssues].map(i => ({
                id: i.id || Math.random().toString(36).substr(2, 9),
                type: i.type || 'Quality',
                severity: (i.severity || 'low').toLowerCase() as any,
                file: i.file_path || 'snippet',
                line: i.line_start || 0,
                message: i.title || i.description || 'Issue detected',
                suggestion: i.suggestion
            }));
        })(),
        summary: jobStatus?.message
    };

    return (
        <div className="min-h-screen bg-background pb-12">
            <Header />

            <main className="container mx-auto px-6 py-8 space-y-8">
                {/* Back Navigation */}
                <Link to="/projects" className="inline-flex items-center text-text-muted hover:text-primary transition-colors mb-2 group">
                    <ArrowLeft className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" />
                    {t('projectDetails.backToProjects')}
                </Link>

                {/* Project Header */}
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-4 mb-2">
                            <h1 className="text-4xl font-bold text-text-primary font-mono lowercase tracking-tighter">devlint/{project.name}</h1>
                            <Badge severity={project.is_active ? 'success' : 'low'}>
                                {project.is_active ? t('projectDetails.active') : t('projectDetails.archived')}
                            </Badge>
                        </div>
                        <p className="text-text-muted font-mono max-w-2xl text-sm italic">
                            {project.description}
                        </p>
                        
                        <div className="flex flex-wrap items-center gap-6 mt-6 text-xs text-text-secondary font-mono">
                            {/* CUSTOM BRANCH SELECTOR */}
                            <div className="relative" ref={branchMenuRef}>
                                <button 
                                    onClick={() => setIsBranchMenuOpen(!isBranchMenuOpen)}
                                    className="flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-asymmetric-sm border border-primary/20 transition-all hover:bg-primary/20 font-bold"
                                >
                                    <GitBranch className="h-3.5 w-3.5" />
                                    <span>Branch: {selectedBranch}</span>
                                    <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${isBranchMenuOpen ? 'rotate-180' : ''}`} />
                                </button>

                                {isBranchMenuOpen && (
                                    <div className="absolute top-full left-0 mt-2 w-56 bg-surface border border-primary/30 rounded-asymmetric-md shadow-2xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2">
                                        <div className="p-2 border-b border-border bg-surface-hover/50">
                                            <span className="text-[10px] uppercase tracking-widest font-bold opacity-50">Select Branch</span>
                                        </div>
                                        <div className="max-h-64 overflow-y-auto">
                                            {branches ? (branches as any).map((b: any) => {
                                                const name = typeof b === 'string' ? b : b.name;
                                                return (
                                                    <button
                                                        key={name}
                                                        onClick={() => {
                                                            setSelectedBranch(name);
                                                            setIsBranchMenuOpen(false);
                                                        }}
                                                        className={`w-full flex items-center justify-between px-4 py-2.5 text-left text-xs font-mono transition-colors hover:bg-primary/10 ${selectedBranch === name ? 'bg-primary/20 text-primary font-bold' : 'text-text-secondary'}`}
                                                    >
                                                        <span className="truncate">{name}</span>
                                                        {selectedBranch === name && <CheckCircle2 className="h-3 w-3" />}
                                                    </button>
                                                );
                                            }) : (
                                                <div className="px-4 py-4 text-center">
                                                    <Loader2 className="h-4 w-4 animate-spin mx-auto opacity-30" />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center gap-2">
                                <Code className="h-3.5 w-3.5" />
                                TypeScript
                            </div>
                            <div className="flex items-center gap-2">
                                <Activity className="h-3.5 w-3.5 opacity-50" />
                                Último Análisis: {localData?.lastUpdated ? new Date(localData.lastUpdated).toLocaleString() : 'Nunca'}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <ExportButton data={reportData} />
                        <Button 
                            variant="primary"
                            corners="asymmetric" 
                            onClick={() => handleRunAnalysis()} 
                            disabled={isAnalyzing || (!!activeJobId && jobStatus?.status !== 'completed' && jobStatus?.status !== 'failed') || !project.repository_url}
                            className="relative overflow-hidden bg-gradient-to-br from-white via-white to-cyan-500/30 text-black border border-border shadow-sm hover:shadow-md transition-all font-bold group"
                        >
                            <span className="relative z-10 flex items-center">
                                {isAnalyzing || (!!activeJobId && jobStatus?.status !== 'completed' && jobStatus?.status !== 'failed') ? <Loader2 className="animate-spin h-4 w-4 mr-2" /> : <Sparkles className="h-4 w-4 mr-2 text-cyan-600" />}
                                Full AI Pro Analysis
                            </span>
                            <div className="absolute bottom-0 right-0 w-8 h-8 bg-cyan-500/20 blur-xl group-hover:bg-cyan-500/40 transition-colors" />
                        </Button>
                        <Button variant="ghost" corners="asymmetric" onClick={() => setIsSnippetModalOpen(true)}>
                            <Code className="h-4 w-4 mr-2" />
                            Snippet Analysis
                        </Button>
                        <Button 
                            variant="primary" 
                            corners="asymmetric-inverse" 
                            onClick={() => handleRunAnalysis(['*'])} 
                            disabled={isAnalyzing || (!!activeJobId && jobStatus?.status !== 'completed' && jobStatus?.status !== 'failed') || !project.repository_url}
                            className="bg-white text-black border border-border hover:bg-neutral-50 shadow-sm font-bold"
                        >
                            {isAnalyzing || (!!activeJobId && jobStatus?.status !== 'completed' && jobStatus?.status !== 'failed') ? <Loader2 className="animate-spin h-4 w-4 mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
                            Quick Scan
                        </Button>
                    </div>
                </div>

                <DirectCodeAnalysisModal
                    isOpen={isSnippetModalOpen}
                    onClose={() => setIsSnippetModalOpen(false)}
                    projectId={projectId}
                    initialCode={droppedCode}
                    fileName={droppedFileName}
                />

                {/* Job Status Banner */}
                {activeJobId && jobStatus && (
                    <Card padding="md" className={`transition-all duration-500 ${jobStatus.status === 'completed' ? 'border-success/50 bg-success/5' : 'border-primary/30 bg-primary/5 animate-pulse'}`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className={`p-2 rounded-full ${jobStatus.status === 'completed' ? 'bg-success/20' : 'bg-primary/20'}`}>
                                    {jobStatus.status === 'completed' ? (
                                        <CheckCircle2 className="h-5 w-5 text-success" />
                                    ) : (
                                        <Loader2 className="h-5 w-5 text-primary animate-spin" />
                                    )}
                                </div>
                                <div>
                                    <h3 className={`text-sm font-bold font-mono uppercase ${jobStatus.status === 'completed' ? 'text-success' : 'text-text-primary'}`}>
                                        {jobStatus.status === 'completed' ? 'Análisis Completado' : 'Análisis en Progreso'}
                                    </h3>
                                    <p className="text-xs text-text-muted font-mono">
                                        {jobStatus.status === 'completed' ? 'Gemini ha terminado de revisar tu código.' : 
                                         jobStatus.status === 'processing' ? 'Gemini está escaneando tus archivos...' : 'En cola...'}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-6">
                                {jobStatus.status === 'completed' ? (
                                    <div className="flex items-center gap-3">
                                        <Button variant="ghost" size="sm" onClick={() => setActiveJobId(null)} className="text-[10px] uppercase font-bold">Descartar</Button>
                                        <Button variant="primary" size="sm" corners="asymmetric-inverse" onClick={() => setIsResultsModalOpen(true)}>
                                            <FileText className="h-3.5 w-3.5 mr-2" />
                                            Ver Resultados
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="text-right">
                                        <div className="text-lg font-bold text-primary font-mono">{jobStatus.progress}%</div>
                                        <div className="w-32 h-1.5 bg-border rounded-full mt-1 overflow-hidden">
                                            <div 
                                                className="h-full bg-primary transition-all duration-500" 
                                                style={{ width: `${jobStatus.progress}%` }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card>
                )}

                {/* Metrics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card padding="md" corners="asymmetric" className="bg-surface/50 border-success/20">
                        <div className="flex justify-between items-start mb-2">
                            <span className="text-text-secondary text-xs font-mono uppercase tracking-widest">{t('projectDetails.healthScore')}</span>
                            <Activity className="h-4 w-4 text-success" />
                        </div>
                        <div className={`text-3xl font-bold font-mono ${(localStats.healthScore || summary?.health_score || 0) >= 70 ? 'text-success' : 'text-error'}`}>
                            {(localStats.healthScore || summary?.health_score || 0).toFixed(0)}%
                        </div>
                        <div className="text-[10px] text-text-muted mt-1 uppercase font-mono tracking-tighter">Calidad Global</div>
                    </Card>

                    <Card padding="md" corners="asymmetric-inverse" className="border-warning/20">
                        <div className="flex justify-between items-start mb-2">
                            <span className="text-text-secondary text-xs font-mono uppercase tracking-widest">{t('projectDetails.totalIssues')}</span>
                            <AlertTriangle className="h-4 w-4 text-warning" />
                        </div>
                        <div className="text-3xl font-bold text-warning font-mono">
                            {(localStats.totalIssues || summary?.total_issues_found || 0)}
                        </div>
                        <div className="flex gap-2 mt-2">
                            <Badge severity="critical" size="sm">{(localStats.critical || summary?.issues_by_severity?.critical || 0)} Crit</Badge>
                        </div>
                    </Card>

                    <Card padding="md" corners="asymmetric" className="border-primary/20">
                        <div className="flex justify-between items-start mb-2">
                            <span className="text-text-secondary text-xs font-mono uppercase tracking-widest">Cobertura</span>
                            <Shield className="h-4 w-4 text-primary" />
                        </div>
                        <div className="text-3xl font-bold text-primary font-mono">{(summary?.coverage || 0).toFixed(1)}%</div>
                        <div className="text-[10px] text-text-muted mt-1 uppercase font-mono tracking-tighter">Estimación IA</div>
                    </Card>

                    <Card padding="md" corners="asymmetric-inverse" className="border-error/20">
                        <div className="flex justify-between items-start mb-2">
                            <span className="text-text-secondary text-xs font-mono uppercase tracking-widest">Deuda</span>
                            <Bug className="h-4 w-4 text-error" />
                        </div>
                        <div className="text-3xl font-bold text-text-primary font-mono">{summary?.technical_debt_human_readable || '--'}</div>
                        <div className="text-[10px] text-text-muted mt-1 uppercase font-mono tracking-tighter">Resolución</div>
                    </Card>
                </div>

                {/* View Switcher con Iconos de la App */}
                <div className="flex border-b border-border mt-8">
                    <button
                        onClick={() => setActiveTab('files')}
                        className={`px-8 py-4 font-mono text-sm transition-all border-b-2 uppercase tracking-widest flex items-center gap-2 ${activeTab === 'files' ? 'border-primary text-primary font-bold bg-primary/5' : 'border-transparent text-text-muted hover:text-text-primary'}`}
                    >
                        <FileCode className={`h-4 w-4 ${activeTab === 'files' ? 'text-primary' : 'text-text-muted'}`} />
                        {t('projectDetails.filesAnalysis')}
                    </button>
                    <button
                        onClick={() => setActiveTab('analysis')}
                        className={`px-8 py-4 font-mono text-sm transition-all border-b-2 uppercase tracking-widest flex items-center gap-2 ${activeTab === 'analysis' ? 'border-primary text-primary font-bold bg-primary/5' : 'border-transparent text-text-muted hover:text-text-primary'}`}
                    >
                        <BarChart2 className={`h-4 w-4 ${activeTab === 'analysis' ? 'text-primary' : 'text-text-muted'}`} />
                        Análisis Avanzado
                    </button>
                </div>

                {activeTab === 'files' ? (
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                        {/* File Tree / List */}
                        <div className="lg:col-span-3 space-y-4">
                            {repoFullName && (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                    <div className="md:col-span-1">
                                        <FileExplorer 
                                            repoFullName={repoFullName} 
                                            branch={selectedBranch} 
                                            onFileSelect={handleFileSelect} 
                                            onAnalyze={(path) => handleRunAnalysis([path])}
                                        />
                                    </div>
                                    <div className="md:col-span-2 flex flex-col gap-4">
                                        <div className="flex items-center justify-between">
                                            <h2 className="text-sm font-bold text-text-primary font-mono uppercase tracking-widest flex items-center gap-2">
                                                <Activity className="h-4 w-4 text-primary" />
                                                Resultados por Archivo
                                            </h2>
                                            <div className="w-48">
                                                <Input placeholder="Filtrar..." icon={<Search className="h-3.5 w-3.5" />} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                                            </div>
                                        </div>
                                        <Card padding="none" className="overflow-hidden border-border/50 h-full bg-surface/20">
                                            {isReportsLoading ? (
                                                <div className="p-12 text-center text-text-muted font-mono flex flex-col items-center gap-3">
                                                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                                    Cargando reportes...
                                                </div>
                                            ) : files.length === 0 ? (
                                                <div className="p-12 text-center text-text-muted font-mono flex flex-col items-center gap-4">
                                                    <p className="max-w-[250px]">No hay análisis guardados. Escanea el repo completo para ver resultados.</p>
                                                    <Button variant="primary" size="sm" onClick={() => handleRunAnalysis()}>Analizar Repo</Button>
                                                </div>
                                            ) : (
                                                <table className="w-full text-left border-collapse">
                                                    <thead className="bg-surface-hover/50 border-b border-border font-mono">
                                                        <tr>
                                                            <th className="p-4 text-[10px] text-text-secondary font-bold uppercase tracking-widest">Archivo</th>
                                                            <th className="p-4 text-[10px] text-text-secondary font-bold uppercase tracking-widest">Issues</th>
                                                            <th className="p-4 text-[10px] text-text-secondary font-bold uppercase tracking-widest text-right">Acciones</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-border/50 font-mono">
                                                        {filteredFiles.map((file, idx) => (
                                                            <tr key={idx} className="hover:bg-primary/5 transition-all group">
                                                                <td className="p-4">
                                                                    <div className="flex items-center gap-3">
                                                                        <FileCode className="h-4 w-4 text-text-muted group-hover:text-primary" />
                                                                        <span className="text-sm text-text-primary truncate max-w-[200px]">{file.name}</span>
                                                                    </div>
                                                                </td>
                                                                <td className="p-4">
                                                                    <Badge severity={(file.issues || 0) > 5 ? 'critical' : (file.issues || 0) > 2 ? 'high' : 'medium'} size="sm">
                                                                        {(file.issues || 0)}
                                                                    </Badge>
                                                                </td>
                                                                <td className="p-4 text-right flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-all">
                                                                    <Button variant="ghost" size="sm" onClick={() => handleFileSelect(file.name)} className="h-7 text-[10px] uppercase font-bold">Código</Button>
                                                                    <Button variant="ghost" size="sm" onClick={() => { setSelectedFileFilter(file.name); setActiveTab('analysis'); }} className="h-7 text-[10px] uppercase font-bold">Detalles</Button>
                                                                    <Button 
                                                                        variant="ghost" 
                                                                        size="sm" 
                                                                        onClick={() => handleRunAnalysis([file.name])} 
                                                                        className="h-7 text-[10px] uppercase font-bold text-primary"
                                                                    >
                                                                        <BrainCircuit className="h-3 w-3 mr-1" /> IA
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            )}
                                        </Card>
                                    </div>
                                </div>
                            )}

                            {!repoFullName && (
                                <Card padding="none" className="overflow-hidden border-border/50">
                                    <div className="flex items-center justify-between p-4 border-b border-border">
                                        <h2 className="text-xl font-semibold text-text-primary font-mono flex items-center gap-2">
                                            <Folder className="h-5 w-5 text-primary" />
                                            Local Files
                                        </h2>
                                    </div>
                                    <div className="p-12 text-center text-text-muted font-mono flex flex-col items-center gap-4">
                                        <p>No hay datos. Sube un archivo o vincula GitHub.</p>
                                        <DropZone onFileRead={handleFileDrop} />
                                    </div>
                                </Card>
                            )}
                        </div>

                        {/* Recent Issues Sidebar */}
                        <div className="space-y-4">
                            <h2 className="text-lg font-bold text-text-primary font-mono uppercase tracking-tighter flex items-center gap-2">
                                <Bug className="h-4 w-4 text-error" />
                                Critical Fixes
                            </h2>
                            <div className="space-y-3">
                                {(() => {
                                    const backendIssues = reportsData?.items?.[0]?.issues || [];
                                    const localIssues = localData?.aggregatedIssues || [];
                                    const allIssues = [...localIssues, ...backendIssues].filter(i => (i.severity || '').toLowerCase() === 'critical').slice(0, 10);
                                    if (allIssues.length === 0) return <div className="p-8 text-center border border-dashed border-border rounded-xl opacity-50 bg-surface/10"><Shield className="h-8 w-8 text-success mx-auto mb-2 opacity-20" /><p className="text-[10px] font-mono uppercase">Limpio</p></div>;
                                    return allIssues.map((issue, idx) => (
                                        <Card key={issue.id || idx} padding="sm" variant="hover" className="cursor-pointer border-l-4 border-l-error bg-surface/30">
                                            <div className="flex items-start gap-3">
                                                <AlertTriangle className="h-3.5 w-3.5 text-error mt-0.5" />
                                                <div className="min-w-0">
                                                    <p className="text-xs font-bold text-text-primary truncate">{issue.title || 'Issue'}</p>
                                                    <p className="text-[9px] text-text-muted font-mono uppercase truncate mt-1">{(issue.file_path || 'snippet').split('/').pop()} • L{issue.line_start || 0}</p>
                                                </div>
                                            </div>
                                        </Card>
                                    ));
                                })()}
                            </div>
                            <div className="pt-6 mt-6 border-t border-border/50">
                                <h3 className="text-[10px] font-bold text-text-secondary font-mono uppercase mb-3 tracking-widest">Escaneo rápido</h3>
                                <DropZone onFileRead={handleFileDrop} className="h-40 p-4" />
                            </div>
                        </div>
                    </div>
                ) : (
                    <AdvancedAnalysis projectId={projectId} fileFilter={selectedFileFilter} onClearFilter={() => setSelectedFileFilter(null)} />
                )}

                {/* Deep Analysis Results Modal */}
                <Modal 
                    isOpen={isResultsModalOpen} 
                    onClose={() => setIsResultsModalOpen(false)}
                    title="Análisis Profundo - Gemini AI"
                    size="lg"
                >
                    <div className="space-y-6 pr-2 custom-scrollbar">
                        <div className="p-5 bg-primary/10 rounded-asymmetric-sm border border-primary/20 flex items-start gap-4">
                            <Shield className="h-6 w-6 text-primary mt-1 shrink-0" />
                            <div>
                                <h4 className="text-sm font-bold text-primary font-mono uppercase tracking-widest">Resumen Ejecutivo</h4>
                                <p className="text-xs text-text-secondary font-mono mt-2 leading-relaxed opacity-80">
                                    Gemini ha procesado los archivos de la rama <span className="text-primary font-bold underline">{selectedBranch}</span>. 
                                    A continuación se presentan los hallazgos semánticos y sugerencias de arquitectura.
                                </p>
                            </div>
                        </div>

                        <div className="space-y-6">
                            {jobStatus?.message?.split('###').filter(Boolean).map((section, idx) => {
                                const [title = '', ...content] = section.trim().split('\n');
                                const isError = title.toLowerCase().includes('error');
                                const cleanContent = content.join('\n').trim();
                                
                                return (
                                    <div key={idx} className={`border rounded-asymmetric-md overflow-hidden bg-surface/40 shadow-sm transition-colors ${isError ? 'border-error/50' : 'border-border/50'}`}>
                                        <div className={`p-3.5 border-b flex items-center justify-between ${isError ? 'bg-error/5 border-error/20' : 'bg-surface-hover/80 border-border/50'}`}>
                                            <div className="flex items-center gap-2">
                                                {isError ? (
                                                    <AlertTriangle className="h-4 w-4 text-error" />
                                                ) : (
                                                    <FileCode className="h-4 w-4 text-primary" />
                                                )}
                                                <span className={`text-xs font-bold font-mono uppercase tracking-tight ${isError ? 'text-error' : 'text-text-primary'}`}>
                                                    {(title || '').trim()}
                                                </span>
                                            </div>
                                            <Badge severity={isError ? 'critical' : 'info'} size="sm" className="font-mono text-[9px]">
                                                {isError ? 'Error IA' : 'Análisis IA'}
                                            </Badge>
                                        </div>
                                        <div className="p-5">
                                            <div className={`text-[12px] font-mono whitespace-pre-wrap leading-relaxed ${isError ? 'text-error/80' : 'text-text-secondary'}`}>
                                                {cleanContent}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        
                        {!jobStatus?.message && (
                            <div className="p-20 text-center flex flex-col items-center gap-4">
                                <Loader2 className="h-10 w-10 animate-spin text-primary opacity-50" />
                                <p className="text-sm font-mono text-text-muted animate-pulse uppercase tracking-widest">Sincronizando hallazgos...</p>
                            </div>
                        )}
                    </div>
                    
                    <div className="mt-8 pt-4 border-t border-border/30 flex justify-end gap-3">
                        <Button variant="ghost" corners="asymmetric" onClick={() => setIsResultsModalOpen(false)}>
                            Cerrar
                        </Button>
                        <Button variant="primary" corners="asymmetric-inverse" onClick={() => { setIsResultsModalOpen(false); setActiveTab('analysis'); }}>
                            Ver Métricas Avanzadas
                        </Button>
                    </div>
                </Modal>
            </main>
        </div>
    );
}
